import { 
  getLocationVersionHistory, 
  getVersionById 
} from './versionHistoryService';
import {
  getLocationVersion,
  getPublishedVersion,
  getLatestDraftVersion,
  getLatestLocationVersion
} from './versionStatusService';
import { Firestore } from 'firebase/firestore';
import { LocationVersion } from '@/types/campLocation';
import cacheService from '@/services/cacheService';

// Cache keys
const VERSION_HISTORY_KEY_PREFIX = 'locationVersion:history:';
const VERSION_KEY_PREFIX = 'locationVersion:';
const LATEST_VERSION_KEY_PREFIX = 'locationVersion:latest:';
const PUBLISHED_VERSION_KEY_PREFIX = 'locationVersion:published:';

// Cache durations
const VERSION_HISTORY_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const VERSION_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

/**
 * Get cached or fresh location version history
 */
export const getCachedLocationVersionHistory = async (
  db: Firestore,
  locationId: string,
  forceRefresh = false
): Promise<LocationVersion[]> => {
  const cacheKey = `${VERSION_HISTORY_KEY_PREFIX}${locationId}`;
  
  if (!forceRefresh) {
    const cached = cacheService.get<LocationVersion[]>(cacheKey);
    if (cached) {
      console.log(`[locationVersionCacheService] Using cached version history for ${locationId}`);
      return cached;
    }
  }
  
  console.log(`[locationVersionCacheService] Fetching fresh version history for ${locationId}`);
  const versions = await getLocationVersionHistory(db, locationId);
  
  // Cache the result
  cacheService.set(cacheKey, versions, VERSION_HISTORY_CACHE_DURATION, 'medium');
  
  return versions;
};

/**
 * Get a cached or fresh location version by ID
 */
export const getCachedLocationVersion = async (
  db: Firestore,
  versionId: string,
  forceRefresh = false
): Promise<LocationVersion | null> => {
  const cacheKey = `${VERSION_KEY_PREFIX}${versionId}`;
  
  if (!forceRefresh) {
    const cached = cacheService.get<LocationVersion>(cacheKey);
    if (cached) {
      console.log(`[locationVersionCacheService] Using cached version: ${versionId}`);
      return cached;
    }
  }
  
  console.log(`[locationVersionCacheService] Fetching fresh version: ${versionId}`);
  const version = await getLocationVersion(db, versionId);
  
  if (version) {
    cacheService.set(cacheKey, version, VERSION_CACHE_DURATION, 'high');
  }
  
  return version;
};

/**
 * Get a cached or fresh latest location version
 */
export const getCachedLatestLocationVersion = async (
  db: Firestore,
  locationId: string,
  forceRefresh = false
): Promise<LocationVersion | null> => {
  const cacheKey = `${LATEST_VERSION_KEY_PREFIX}${locationId}`;
  
  if (!forceRefresh) {
    const cached = cacheService.get<LocationVersion>(cacheKey);
    if (cached) {
      console.log(`[locationVersionCacheService] Using cached latest version for ${locationId}`);
      return cached;
    }
  }
  
  console.log(`[locationVersionCacheService] Fetching fresh latest version for ${locationId}`);
  const latestVersion = await getLatestLocationVersion(db, locationId);
  
  if (latestVersion) {
    cacheService.set(cacheKey, latestVersion, VERSION_CACHE_DURATION, 'high');
  }
  
  return latestVersion;
};

/**
 * Get a cached or fresh published location version
 */
export const getCachedPublishedVersion = async (
  db: Firestore,
  locationId: string,
  forceRefresh = false
): Promise<LocationVersion | null> => {
  const cacheKey = `${PUBLISHED_VERSION_KEY_PREFIX}${locationId}`;
  
  if (!forceRefresh) {
    const cached = cacheService.get<LocationVersion>(cacheKey);
    if (cached) {
      console.log(`[locationVersionCacheService] Using cached published version for ${locationId}`);
      return cached;
    }
  }
  
  console.log(`[locationVersionCacheService] Fetching fresh published version for ${locationId}`);
  const publishedVersion = await getPublishedVersion(db, locationId);
  
  if (publishedVersion) {
    cacheService.set(cacheKey, publishedVersion, VERSION_CACHE_DURATION, 'high');
  }
  
  return publishedVersion;
};

/**
 * Invalidate all caches related to a location
 */
export const invalidateLocationVersionCache = (locationId: string): void => {
  console.log(`[locationVersionCacheService] Invalidating all caches for location ${locationId}`);
  
  // Clear specific version history cache
  cacheService.clear(`${VERSION_HISTORY_KEY_PREFIX}${locationId}`);
  
  // Clear latest version cache
  cacheService.clear(`${LATEST_VERSION_KEY_PREFIX}${locationId}`);
  
  // Clear published version cache
  cacheService.clear(`${PUBLISHED_VERSION_KEY_PREFIX}${locationId}`);
  
  // Find and clear any individual version caches related to this location
  const allKeys = cacheService.getKeysByPrefix(VERSION_KEY_PREFIX);
  allKeys.forEach(key => {
    const version = cacheService.get<LocationVersion>(key);
    if (version && version.locationRef && 
        (version.locationRef.id === locationId || 
         (typeof version.locationRef === 'string' && version.locationRef.includes(locationId)))) {
      cacheService.clear(key);
    }
  });
};

/**
 * Event-driven update system to replace polling
 */
export const registerVersionChangeHandlers = (): void => {
  window.addEventListener('locationVersionChanged', (event: Event) => {
    const customEvent = event as CustomEvent<{ locationId: string }>;
    if (customEvent.detail && customEvent.detail.locationId) {
      invalidateLocationVersionCache(customEvent.detail.locationId);
    }
  });
};
